<template>
  <div class="geofence-fields">

    <!-- GEOFENCE INFORMATION -->
    <div class="row">
      <div class="small-12 columns">
        <div class="heading">
          <label>GEOFENCE INFORMATION</label>
        </div>
      </div>

      <div class="columns margin-B small-6 padding-R">
        <div class="input-field">
          <label>Geofence Name</label>
          <input type="text" v-model="geofence.name" />
        </div>
      </div>

      <div class="small-6 columns margin-B padding-L">
        <div class="input-field" v-if="geofence.type === 'airport'">
          <label>Airport Code</label>
          <input type="text" v-model="geofence.airportCode" />
        </div>
        <div class="input-field" v-else>
          <label>Airport Association</label>
          <input type="text" :value="airportAssocioation" disabled/>
        </div>
      </div>

      <div class="small-6 columns margin-B padding-R end">
        <div class="input-field">
          <label>
            Navigation Address
            <span v-show="!!centerpoint" class="center-point">({{ centerpoint }})</span>
          </label>
          <div class="autocomplete">
            <input type="text" @keyup="query" v-on:blur="clickOut" :disabled="loading || navigationAddressLoading"
              v-model="navigationAddress" :class="{'input-location':true, 'has-error':!!error.navigationAddress}" />
            <span class="error-msg" v-show="!!error.navigationAddress">{{ error.navigationAddress }}</span>
            <transition name="slide-down">
              <ul class="suggestions" v-show="showSuggestion">
                <li v-for="(item, index) in suggestions" @click="selectLocation(index)"
                  :class="{'empty':item.title === 'Sorry'}" :key="index">
                  <i v-bind:class="['fas', item.icon]"></i>
                  <span>{{item.title + ', ' + item.subtitle}}</span>
                </li>
              </ul>
            </transition>
          </div>
        </div>
      </div>

      <div class="small-6 columns margin-B padding-L end">
        <div class="input-field">
          <label>Overlay Color</label>
          <div class="color-input-box">
            <input type="text" v-model="geofence.overlayColor ? geofence.overlayColor : geofence.color" />
            <div class="palette" v-bind:style="geofence.overlayColor ? {background: '#' + geofence.overlayColor} : {background: '#' + geofence.color}"
              @click="toggleOverlayColor"></div>
            <transition name="slide-down">
              <photoshop-picker v-model="overlayColor" v-show="showOverlayColor"
                class="slide-overlay-color" @ok="selectColor" @cancel="showOverlayColor = false"></photoshop-picker>
            </transition>
          </div>
        </div>
      </div>

      <div class="small-6 columns margin-B padding-R end">
        <div class="input-field">
          <label>Manual Coords (OPTIONAL)</label>
          <input type="text" v-model="manualCoords" :disabled="loading || navigationAddressLoading"
          />
        </div>
      </div>

    </div>

    <!-- KML and CSV UPLOAD/DOWNLOAD -->
    <div class="row">
      <div class="small-5 columns padding-R end">
        <div :class="{'row upload-row':true, 'below':!upload.kml.loading}">
          <div :class="{'upload-box':true, 'has-message':upload.kml.message}">
            <div :class="{'upload-message':true, 'error':!upload.kml.success}" v-show="!upload.kml.loading">{{ upload.kml.message }}</div>

            <b class="upload-file">KML file</b>
            <div class="button warning xsmall" @click="triggerKMLUpload">
              <span v-if="!!geofence.kmlFile && !upload.kml.loading">Replace</span>
              <span v-else-if="!upload.kml.loading">Upload</span>
              <span v-else>Uploading...</span>
            </div>
            <a class="button success xsmall download" :href="geofence.kmlFile" v-if="!!geofence.kmlFile" download>Download</a>
            <input type="file" class="input" ref="kmlUpload" v-show="false" /> <!-- @change="uploadFile" -->
          </div>
        </div>
      </div>

      <div class="small-5 columns padding-L end">
        <div :class="{'row upload-row':true, 'below':!upload.csv.loading}" v-if="geofence.type === 'airport'">
          <div :class="{'upload-box airline':true, 'has-message':upload.csv.message}">
            <div :class="{'upload-message':true, 'error':!upload.csv.success}" v-show="!upload.csv.loading">{{ upload.csv.message }}</div>

            <b class="upload-file">Airlines and Terminal CSV</b>
            <div class="button warning xsmall" @click="triggerCSVUpload">
              <span v-if="!upload.csv.loading">Replace</span>
              <span v-else-if="upload.action === 'upload'">Uploading...</span>
              <span v-else>Replace</span>
            </div>
            <div class="button success xsmall download" @click="downloadCSV">
              <span v-if="!upload.csv.loading">Download</span>
              <span v-else-if="upload.action === 'download'">Downloading...</span>
              <span v-else>Download</span>
            </div>
            <input type="file" class="input" ref="csvUpload" v-show="false"
              @change="uploadFile" />
          </div>
        </div>
      </div>
    </div>
    <transition name="fade">
      <div class="upload-loading-bg" v-show="upload.csv.loading || upload.kml.loading"></div>
    </transition>

    <!-- AIRPORT GEOFENCE FIELDS -->
    <div v-if="geofence.type === 'airport'">
      <geofence-fields-airport :geofence="geofence"
        :loadOtherData="loadOtherData"
        @uploading="uploadingPickupInstruction"></geofence-fields-airport>
    </div>

    <!-- RESTRICTED GEOFENCE FIELDS -->
    <div v-if="geofence.type === 'restricted'">
      <geofence-fields-restricted :geofence="geofence"></geofence-fields-restricted>
    </div>

    <!-- STAGING GEOFENCE FIELDS -->
    <div v-if="geofence.type === 'staging'">
      <geofence-fields-staging :geofence="geofence"></geofence-fields-staging>
    </div>

    <!-- SERVICE AREA GEOFENCE FIELDS -->
    <div v-if="geofence.type === 'servicearea'">
      <geofence-fields-service-area :geofence="geofence"></geofence-fields-service-area>
    </div>

    <div class="row" v-show="formError.show">
      <div class="small-12 columns">
        <div class="error-message">
          Please fix the following errors first before continuing:
          <ul>
            <li v-for="item in formError.items">{{ item }}</li>
          </ul>
        </div>
      </div>
    </div>
    <div :class="['row geofence-controls', geofence.type === 'servicearea' ? 'service-area' : '']">
      <div class="small-2 columns padding-L padding-R" v-if="geofence.type === 'servicearea'">
        <a href="javascript:;" class="button cta xsmall block info" @click="clone(geofence.id)">Clone</a>
      </div>
      <div :class="['small-2 columns padding-R', geofence.type === 'servicearea' ? 'small-offset-4' : 'small-offset-6']" v-if="geofenceStatus !== 'archived'">
        <a href="javascript:;" class="button cta xsmall block warning"
          @click="toggleEnabled">
          <span v-if="geofenceStatus === 'active'">DEACTIVATE</span>
          <span v-else>ACTIVATE</span>
        </a>
      </div>
      <div :class="['small-2 columns padding-L padding-R', geofence.archived ? 'small-offset-8' : geofence.type === 'servicearea' && geofence.status === 'archived' ? 'small-offset-6' : '']">
        <a href="javascript:;" class="button cta xsmall block error"
          @click="toggleArchived">
          <span v-if="geofenceStatus === 'archived'">UNARCHIVE</span>
          <span v-else>ARCHIVE</span>
        </a>
      </div>
      <div class="small-2 columns padding-L">
        <button href="javascript:;" :disabled="uploading.tnc || uploading.tcp"
          :class="{'button cta xsmall block success':true, 'disabled':uploading.tnc || uploading.tcp}"
          @click="uploadKML">SAVE <span class="hideMobile">CHANGES</span></button>
      </div>
    </div>

    <transition name="expand">
      <div class="loader" v-show="loading">
        <div class="modal-table">
          <div class="modal-content">
            <div class="box">
              <div id="scrollHere"></div>
              <div v-if="!!message">
                <i class="far fa-check-circle fa-4x response" v-if="success"></i>
                <i class="far fa-times-circle fa-4x response" v-else></i>
                <span class="message">{{ message }}</span>
                <div class="button cta block" v-if="!success"
                  @click="closeModal">Close</div>
              </div>
              <div v-else>
                <i class="fa fa-spinner fa-spin fa-2x"></i>
                <span>Saving...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
/* global CONFIG */
const request = require('browser-request')
import GeofenceFieldsAirport from './GeofenceFieldsAirport'
import GeofenceFieldsRestricted from './GeofenceFieldsRestricted'
import GeofenceFieldsServiceArea from './GeofenceFieldsServiceArea'
import GeofenceFieldsStaging from './GeofenceFieldsStaging'
import { Photoshop } from 'vue-color'
import CsvExport from 'csvexport'

const exporter = CsvExport.create({
  filename: 'export.csv',
  columns: [ 'Airline Name', 'Terminal', 'Airline Code' ]
})

export default {
  name: 'geofence-fields',
  props: [ 'geofence', 'loadOtherData' ],
  components: {
      GeofenceFieldsAirport, GeofenceFieldsRestricted, GeofenceFieldsServiceArea,
      GeofenceFieldsStaging, 'photoshop-picker': Photoshop
    },
  data () {
    return {
      airportAssocioation: '',
      airportAssocioationLoading: false,
      loading: false,
      message: '',
      success: false,
      upload: {
        kml: {
          loading: false,
          message: '',
          success: false
        },
        csv: {
          loading: false,
          message: '',
          success: false
        },
        target: '',
        action: ''
      },
      navigationAddressObject: '',
      navigationAddress: '',
      navigationAddressLoading: false,
      lastNaviationAddress: '',
      tmpLastNavAddress: '',
      centerpoint: '',
      lastCenterpoint: '',
      // autocomplete: new google.maps.places.AutocompleteService(), // eslint-disable-line no-undef
      // geocoder: new google.maps.Geocoder(), // eslint-disable-line no-undef
      queryToken: null,
      suggestions: [],
      showSuggestion: false,
      clickOutDelay: '',
      overlayColor: '',
      showOverlayColor: false,
      error: {
        navigationAddress: ''
      },
      formError: {
        show: false,
        items: []
      },
      isP2P: false,
      uploading: {
        tnc: false,
        tcp: false
      },
      manualCoords: ''
    }
  },
  computed: {
    geofenceStatus () { return this.$store.state.geofences.filters.status },
    geofenceType () { return this.$store.state.geofences.filters.type }
  },
  methods: {
    getInfo () {
      this.getNavigationAdress()
      if (this.geofence.type !== 'airport') {
        this.getAirportAssocation()
      }
    },
    getNavigationAdress () {
      let self = this
      if (self.navigationAddressLoading) { return }

      self.navigationAddressLoading = true
      self.$sdk.get('/geofences/' + self.geofence.id, {
        token: self.$store.state.session.sessionToken
      }, r => {
        self.navigationAddressLoading = false
        if (r.error) {
          return
        }

        self.tmpLastNavAddress = r.location
        let loc = r.location
        self.navigationAddress = loc.addressLine1
        if (!!loc.addressLine2) { self.navigationAddress += ' ' + loc.addressLine2 }
        self.navigationAddress += ', ' + loc.city + ', ' + loc.state + ' ' + loc.zipCode

        self.lastNaviationAddress = self.navigationAddress
        self.lastCenterpoint = loc.lat + ',' + loc.lng
        self.manualCoords = `${r.lat}, ${r.lng}`
      })
    },
    getAirportAssocation () {
      let self = this
      if (self.airportAssocioationLoading) { return }

      self.airportAssocioationLoading = true
      self.$sdk.get('/geofences/' + self.geofence.id + '/airport', {
        token: self.$store.state.session.sessionToken
      }, r => {
        self.airportAssocioationLoading = false
        if (r.error) {
          console.log(r.error)
          return
        }

        self.airportAssocioation = r.airports[0] ? r.airports[0].name : 'none'
        self.isP2P = r.airports.length <= 0
        self.$store.commit('geofences/setGeofenceIsP2P', {
          id: self.geofence.id,
          value: r.airports.length <= 0
        })
      })
    },
    toggleOverlayColor () {
      this.showOverlayColor = !this.showOverlayColor
      if (this.overlayColorDelay) { clearTimeout(this.overlayColorDelay) }
    },
    selectColor () {
      this.geofence.overlayColor = this.overlayColor.hex.substring(1, this.overlayColor.hex.length)
      this.showOverlayColor = false
    },
    query () {
      let self = this

      let queryText = self.navigationAddress
      if (queryText === '') {
        self.queryToken = null
        self.suggestions = []
        self.showSuggestion = false
        return
      }

      // self.generateQueryToken()

      self.showSuggestion = true
      const restrictedTypes = [
        'locality', 'postalCodePoint', 'administrativeArea',
        'chainQuery', 'categoryQuery'
      ]

      let url = 'https://autosuggest.search.hereapi.com/v1/autosuggest?at={AT}&q={SEARCH}&apiKey={API_KEY}&lang=en-US'
      url = url.replace('{AT}', encodeURIComponent(33.942791 + ',' + -118.410042))
        .replace('{SEARCH}', queryText)
        .replace('{API_KEY}', CONFIG.hereApi.key)
      request.get({
        url: url,
        json: true
      }, function (err, res, body) {
        if (err || body.items.length === 0) {
          self.suggestions = [{
            icon: 'fa-exclamation-triangle',
            title: 'Sorry',
            subtitle: 'We coudn\'t locate "' + queryText + '"'
          }]
          return
        }

        let result = []
        body.items.forEach(function (prediction) {
          if (restrictedTypes.indexOf(prediction.resultType) === -1) {
            let subtitle = prediction.address.label
            result.push({
              id: prediction.id,
              title: prediction.title,
              subtitle: subtitle,
              icon: 'fa-map-marker'
            })
          }
        })

        self.suggestions = result.slice(0, 5)
      })
    },
    clickOut () {
      let self = this
      self.clickOutDelay = setTimeout(function () {
        self.centerpoint = self.lastCenterpoint
        if (self.navigationAddress !== self.lastNaviationAddress) {
          self.centerpoint = ''
          self.manualCoords = ''
        }
        self.showSuggestion = false
      }, 300)
    },
    selectLocation (index) {
      let self = this
      let place = self.suggestions[index]
      self.queryToken = null

      clearTimeout(self.clickOutDelay)
      self.showSuggestion = false

      let url = 'https://lookup.search.hereapi.com/v1/lookup?source=sharing&id={LOCATION_ID}&locationattributes=tz&apiKey={API_KEY}&lang=en-US'
      url = url.replace('{LOCATION_ID}', place.id)
        .replace('{API_KEY}', CONFIG.hereApi.key)
      request.get({
        url: url,
        json: true
      }, function (err, res, body) {
        if (err) {
          console.log('geocode ERROR', results, status)
          self.loading = false
          return
        }

        let result = body
        let lat = result.position.lat
        let lng = result.position.lng
        self.centerpoint = lat + ', ' + lng
        self.manualCoords = lat + ', ' + lng

        let navigationAddressObject = {
          title: result.title,
          address1: result.address.street || result.title,
          address2: result.address.houseNumber || result.address.district,
          city: result.address.city,
          state: result.address.state,
          zip: result.address.postalCode,
          country: result.address.countryCode,
          lat: lat,
          lng: lng
        }

        self.geofence.addressLine1 = self.navigationAddressObject.address1
        self.geofence.addressLine2 = self.navigationAddressObject.address2
        self.geofence.city = self.navigationAddressObject.city
        self.geofence.state = self.navigationAddressObject.state
        self.geofence.zipCode = self.navigationAddressObject.zip
        self.geofence.lat = self.navigationAddressObject.lat
        self.geofence.lng = self.navigationAddressObject.lng

        self.navigationAddressObject = navigationAddressObject
        self.navigationAddress = self.navigationAddressObject.title
        self.lastNaviationAddress = self.navigationAddress
        self.lastCenterpoint = self.centerpoint
      })
    },
    toggleEnabled () {
      let self = this
      let url = self.$store.state.geofences.filters.status === 'active' ? '/deactivate' : '/activate'

      let isActivate = !self.geofence.enabled
      if (isActivate && !self.isGeofenceValid()) {
        return
      }

      self.loading = true
      self.message = ''

      setTimeout(function () { self.scrollToLoading() }, 200)

      self.$sdk.post('/geofences/' + self.geofence.id + url, {
        token: self.$store.state.session.sessionToken
      }, res => {
        setTimeout(function () {
          self.loading = false
        }, 2000)
        if (res.error) {
          self.message = res.error.msg
          self.success = false
          return
        }

        self.message = self.geofence.enabled ? 'Geofence Deactivated!' : 'Geofence Activated!'
        self.success = true
        self.$store.commit('geofences/setRefreshList', true)
      })
    },
    toggleArchived () {
      let self = this
      let url = self.geofenceStatus === 'archived' ? '/unarchive' : '/archive'

      let isActivate = !self.geofence.enabled
      if (isActivate && !self.isGeofenceValid()) {
        return
      }

      if (self.loading) { return true }

      self.loading = true
      self.message = ''

      setTimeout(function () { self.scrollToLoading() }, 200)

      self.$sdk.post('/geofences/' + self.geofence.id + url, {
        token:self.$store.state.session.sessionToken
      }, res => {
        setTimeout(function () {
          self.loading = false
        }, 2000)
        if (res.error) {
          self.message = res.error.msg
          self.success = false
          return
        }

        self.message = self.geofence.enabled ? 'Geofence Unarchived!' : 'Geofence Archived!'
        self.success = true
        self.$store.commit('geofences/setRefreshList', true)
      })
    },
    save () {
      let self = this
      if (self.loading) { return true }

      // validation
      if (!self.isGeofenceValid()) { return }

      let data = self.geofence
      data.token = self.$store.state.session.sessionToken
      if (self.geofence.type === 'airport') {
        data.airportPickupInstructions = self.geofence.pickupInstructions.instructions
      }

      if (data.stagingLots) { delete data.stagingLots }
      if (data.restrictedAreas) { delete data.restrictedAreas }
      if (data.serviceAreas) { delete data.serviceAreas }

      data.token = self.$store.state.session.sessionToken
      self.loading = true
      self.message = ''

      setTimeout(function () { self.scrollToLoading() }, 200)

      data.location = {
        address1: self.tmpLastNavAddress.addressLine1,
        address2: self.tmpLastNavAddress.addressLine2,
        city: self.tmpLastNavAddress.city,
        state: self.tmpLastNavAddress.state,
        zip: self.tmpLastNavAddress.zipCode,
        lat: self.tmpLastNavAddress.lat,
        lng: self.tmpLastNavAddress.lng
      }
      if (self.navigationAddressObject) {
        data.location = {
          address1: self.navigationAddressObject.address1,
          address2: self.navigationAddressObject.address2,
          city: self.navigationAddressObject.city,
          state: self.navigationAddressObject.state,
          zip: self.navigationAddressObject.zip,
          lat: self.navigationAddressObject.lat,
          lng: self.navigationAddressObject.lng
        }
      }
      data.centerPoint = data.lat + ', ' + data.lng
      if (!data.lat && self.tmpLastNavAddress && self.tmpLastNavAddress.lat) data.centerPoint = `${self.tmpLastNavAddress.lat}, ${self.tmpLastNavAddress.lng}`
      if (self.manualCoords) data.centerPoint = self.manualCoords

      self.$sdk.post('/geofences/' + self.geofence.id, data, res => {
        if (res.error) {
          self.loading = false
          self.formError.items.push(res.error.message)
          self.formError.show = true
          return
        }

        if (self.geofence.type === 'staging' || self.geofence.type === 'restricted') {
          let services = this.$store.state.services.list.filter(x => x.brandId && x.enabled)
          let saveServices = []
          services.forEach(service => {
            saveServices.push({
              serviceTypeId: service.id,
              enabled: data.serviceTypeIds.indexOf(service.id) > -1
            })
          })
          self.$sdk.post('/servicearea/' + self.geofence.id + '/pricing', {
            token: self.$store.state.session.sessionToken,
            services: saveServices
          }, res => {
            if (res.error) {
              self.loading = false
              self.formError.items.push(res.error.message)
              self.formError.show = true
              return
            }

            self.message = 'Save successfully!'
            self.success = true
            setTimeout(function () {
              self.loading = false
            }, 2000)
          }, '2.0')
        } else {
          self.message = 'Save successfully!'
          self.success = true
          setTimeout(function () {
            self.loading = false
          }, 2000)
        }
      })
    },
    closeModal () {
      self.loading = false
      self.message = ''
    },
    scrollToLoading () {
      let elmnt = document.getElementById('scrollHere')
      elmnt.scrollIntoView({behavior: 'smooth', block: 'start'})
    },
    triggerCSVUpload () {
      this.upload.target = 'csv'
      this.upload.action = 'upload'
      this.$refs.csvUpload.click()
    },
    triggerKMLUpload () {
      this.upload.target = 'kml'
      this.upload.action = 'upload'
      this.$refs.kmlUpload.click()
    },
    downloadCSV () {
      let self = this
      self.upload.target = 'csv'
      this.upload.action = 'download'
      self.upload.csv.loading = true
      self.$sdk.get('/geofences/' + self.geofence.id + '/airlines', {
        token: self.$store.state.session.sessionToken
      }, r => {
        self.upload[self.upload.target].loading = false
        if (r.error) {
          self.formError.items.push(res.error.message)
          self.formError.show = true
          return
        }

        let airlines = []
        for (let i in r.list) {
          airlines.push({
            'Airline Name': r.list[i].name,
            'Terminal': r.list[i].terminalInfo,
            'Airline Code': r.list[i].code
          })
        }
        self.upload[self.upload.target].message = ''
        self.upload[self.upload.target].success = true
        exporter.downloadCsv(airlines)
      })
    },
    uploadKML () {
      this.formError = {
        show: false,
        items: []
      }
      if (!this.$refs.kmlUpload.files.length) {
        this.save()
        return
      }

      let self = this
      let file = self.$refs.kmlUpload.files[0]
      let fd = new FormData()
      fd.append('pic', file)

      self.upload[self.upload.target].loading = true
      self.upload[self.upload.target].message = ''

      self.$http.post(CONFIG.mediaHost + '/api/upload', fd, {
        header: {'Content-Type': 'multipart/form-data'}
      }).then(function (res) {
        if (!res.body.success) {
          self.loading = false
          self.upload[self.upload.target].message = 'Upload fail. Please try again.'
          self.upload[self.upload.target].success = false
          self.upload[self.upload.target].loading = false
          return
        }

        self.updateKML(res.body.data.url)
      }, function (res) {
        console.log('error', res)
      })
    },
    uploadFile (e) {
      if (!e.target.files.length) { return }

      let self = this
      let file = e.target.files[0]
      let fd = new FormData()
      fd.append('pic', file)

      self.upload[self.upload.target].loading = true
      self.upload[self.upload.target].message = ''

      self.$http.post(CONFIG.mediaHost + '/api/upload', fd, {
        header: {'Content-Type': 'multipart/form-data'}
      }).then(function (res) {
        if (!res.body.success) {
          self.upload[self.upload.target].message = 'Upload fail. Please try again.'
          self.upload[self.upload.target].success = false
          self.upload[self.upload.target].loading = false
          return
        }

        if (self.upload.target === 'kml') {
          self.updateKML(res.body.data.url)
        }
        if (self.upload.target === 'csv') {
          self.updateAirlineCSV(res.body.data.url)
        }

      }, function (res) {
        console.log('error', res)
      })
    },
    updateKML (url) {
      let self = this
      self.$sdk.post('/geofences/' + self.geofence.id + '/polygon', {
        token: self.$store.state.session.sessionToken,
        kmlUrl: url,
        lat: self.geofence.lat,
        lng: self.geofence.lng
      }, r => {
        self.upload[self.upload.target].loading = false
        if (r.error) {
          self.loading = false
          self.formError.items.push(r.error.message)
          self.formError.show = true
          return
        }

        self.geofence.kmlFile = url
        self.upload[self.upload.target].message = 'KML update success.'
        self.upload[self.upload.target].success = true

        self.save()
      })
    },
    updateAirlineCSV (url) {
      let self = this
      self.$sdk.post('/airports/' + self.geofence.id + '/airlines', {
        token: self.$store.state.session.sessionToken,
        csvUrl: url
      }, r => {
        self.upload[self.upload.target].loading = false
        if (r.error) {
          self.upload[self.upload.target].message = r.error.message
          self.upload[self.upload.target].success = false
          return
        }

        self.upload[self.upload.target].message = 'Airline CSV update success.'
        self.upload[self.upload.target].success = true
      })
    },
    isGeofenceValid () {
      let self = this
      let hasError = false
      self.formError = {
        show: false,
        items: []
      }

      // service area validation
      if (self.geofence.type === 'servicearea') {
        // check service rate settings
        if ((!self.isP2P && self.geofence.SharedAirportFee === null) ||
            self.geofence.SharedMileageRate === null ||
            self.geofence.SharedFare === null ||
            self.geofence.SharedMileageThreshold === null ||
            self.geofence.SharedFlat2ndPAXFee === null ||
            self.geofence.SharedFlatAddPAXFee === null ||
            self.geofence.SharedPassengerMileageRate === null ||
            self.geofence.SharedMaxLuggage === null ||
            self.geofence.SharedDiscount === null ||
            self.geofence.SharedRoundtrip === null ||
            (!self.isP2P && self.geofence.OpoliAirportFee === null) ||
            self.geofence.OpoliMileageRate === null ||
            self.geofence.OpoliFare === null ||
            self.geofence.OpoliMileageThreshold === null ||
            self.geofence.OpoliFlat2ndPAXFee === null ||
            self.geofence.OpoliFlatAddPAXFee === null ||
            self.geofence.OpoliPassengerMileageRate === null ||
            self.geofence.OpoliMaxLuggage === null ||
            self.geofence.OpoliDiscount === null ||
            self.geofence.OpoliRoundtrip === null ||
            (!self.isP2P && self.geofence.BlackAirportFee === null) ||
            self.geofence.BlackMileageRate === null ||
            self.geofence.BlackFare === null ||
            self.geofence.BlackMileageThreshold === null ||
            self.geofence.BlackFlat2ndPAXFee === null ||
            self.geofence.BlackFlatAddPAXFee === null ||
            self.geofence.BlackPassengerMileageRate === null ||
            self.geofence.BlackMaxLuggage === null ||
            self.geofence.BlackDiscount === null ||
            self.geofence.BlackRoundtrip === null ||
            (!self.isP2P && self.geofence.SUVAirportFee === null) ||
            self.geofence.SUVMileageRate === null ||
            self.geofence.SUVFare === null ||
            self.geofence.SUVMileageThreshold === null ||
            self.geofence.SUVFlat2ndPAXFee === null ||
            self.geofence.SUVFlatAddPAXFee === null ||
            self.geofence.SUVPassengerMileageRate === null ||
            self.geofence.SUVMaxLuggage === null ||
            self.geofence.SUVDiscount === null ||
            self.geofence.SUVRoundtrip === null ||
            (!self.isP2P && self.geofence.PrivateShuttleAirportFee === null) ||
            self.geofence.PrivateShuttleMileageRate === null ||
            self.geofence.PrivateShuttleFare === null ||
            self.geofence.PrivateShuttleMileageThreshold === null ||
            self.geofence.PrivateShuttleFlat2ndPAXFee === null ||
            self.geofence.PrivateShuttleFlatAddPAXFee === null ||
            self.geofence.PrivateShuttlePassengerMileageRate === null ||
            self.geofence.PrivateShuttleMaxLuggage === null ||
            self.geofence.PrivateShuttleDiscount === null ||
            self.geofence.PrivateShuttleRoundtrip === null) {
          hasError = true
          self.formError.items.push('Empty fields are not allowed for SERVICE RATE SETTINGS.')
        }

        if (self.geofence.SharedProviderMileageRate === null ||
            self.geofence.SharedProviderBaseFee === null ||
            self.geofence.SharedMileageThreshold === null ||
            self.geofence.SharedProviderFlat2ndPAXFee === null ||
            self.geofence.SharedProviderFlatAddPAXFee === null ||
            self.geofence.SharedProviderDiscount === null ||
            self.geofence.OpoliProviderMileageRate === null ||
            self.geofence.OpoliProviderBaseFee === null ||
            self.geofence.OpoliMileageThreshold === null ||
            self.geofence.OpoliProviderFlat2ndPAXFee === null ||
            self.geofence.OpoliProviderFlatAddPAXFee === null ||
            self.geofence.OpoliProviderDiscount === null ||
            self.geofence.BlackProviderMileageRate === null ||
            self.geofence.BlackProviderBaseFee === null ||
            self.geofence.BlackMileageThreshold === null ||
            self.geofence.BlackProviderFlat2ndPAXFee === null ||
            self.geofence.BlackProviderFlatAddPAXFee === null ||
            self.geofence.BlackProviderDiscount === null ||
            self.geofence.SUVProviderMileageRate === null ||
            self.geofence.SUVProviderBaseFee === null ||
            self.geofence.SUVMileageThreshold === null ||
            self.geofence.SUVProviderFlat2ndPAXFee === null ||
            self.geofence.SUVProviderFlatAddPAXFee === null ||
            self.geofence.SUVProviderDiscount === null ||
            self.geofence.PrivateShuttleProviderMileageRate === null ||
            self.geofence.PrivateShuttleProviderBaseFee === null ||
            self.geofence.PrivateShuttleMileageThreshold === null ||
            self.geofence.PrivateShuttleProviderFlat2ndPAXFee === null ||
            self.geofence.PrivateShuttleProviderFlatAddPAXFee === null ||
            self.geofence.PrivateShuttleProviderDiscount === null) {
          hasError = true
          self.formError.items.push('Empty fields are not allowed for PROVIDER RATE SETTINGS.')
        }

        // check service types
        let services = self.geofence.services
        for (let prop in services) {
          if (!services.hasOwnProperty(prop)) {
            continue
          }
          let service = services[prop]
          if (!service.curbside && !service.meetAndGreet) {
            hasError = true
            self.formError.items.push('All selected SERVICE TYPES should have at least 1 pickup type.')
            break
          }
        }
        // Check License Types
        if (!self.geofence.allowTNC && !self.geofence.allowTCP) {
          hasError = true
          self.formError.items.push('Please select at least 1 LICENSE TYPE.')
        }
      }

      if (!self.geofence.name) {
        hasError = true
        self.formError.items.push('Geofence Name cannot be empty.')
      }
      if (self.geofence.type === 'airport' && !self.geofence.airportCode) {
        hasError = true
        self.formError.items.push('Airport Code cannot be empty.')
      }
      if (!self.navigationAddress) {
        hasError = true
        self.formError.items.push('Navigation Address cannot be empty.')
      }

      // KML file check
      if (self.geofence.kmlFile === null ||
          self.geofence.kmlFile === '') {
        hasError = true
        self.formError.items.push('This Geofence requires a KML FILE.')
      }

      if (hasError) {
        self.formError.show = true
        return false
      }
      return true
    },
    clone (id) {
      this.$store.commit('geofences/setShowCreateGeofence', true)
      this.$store.commit('geofences/setCloneGeofence', id)
    },
    uploadingPickupInstruction (data) {
      this.uploading[data.type] = data.value
    }
  }
}
</script>

<style lang="scss" scoped>
.heading{
  border-bottom: 2px solid #ccc;
  margin-bottom: 10px;

  label{
    font-weight: 600;
    color: #0D79F2;
    padding-top: 5px;
    display: block;
  }
}

.geofence-fields{
  padding: 15px;
  position: relative;
}
.geofence-controls {
  margin-top: 15px;
  border-top: 1px solid rgba(0,0,0,0.15);
  padding-top: 15px;
}

.input-field {
  font-size: 14px;

  & label {
    font-size: 12px;
    font-weight: 700;
    color: #0D79F2;

    .center-point{
      color: #888;
      display: inline-block;
      margin-left: 5px;
    }
  }

  & input, & textarea {
    margin-top: 0;
    padding: 7px 10px;
  }

  & textarea {
    resize: none;
    height: 100px;
  }
}

.checkbox-input {
  & > * {
    display: inline-block;
    vertical-align: middle;
    width: auto;
  }
  & input {
    margin-top: 0;
  }
  & span {
    font-size: 12px;
    font-weight: 700;
    color: #0D79F2;
    height: 16px;
    margin-left: 3px;
  }
}

.autocomplete{
  position: relative;
  width: 100%;
}
.input-location{
  margin: 5px 0 0;
  color: #333;
  line-height: inherit;
  transition: all .15s ease;

  &.loading{ padding-right: 40px; }
  &.has-error{ border-color: #f20d0d; }
  &:disabled{ background: #eee; }
}
.suggestions{
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 150px;
  z-index: 3;
  list-style: none;
  overflow: auto;
  padding: 0;
  margin: 0;
  box-shadow: 1px 1px 4px rgba(0,0,0, .3);

  li{
    background: #f6f6f6;
    padding:8px 10px;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
    border-bottom: 1px solid #bbb;
    color: #333;

    &:last-child{ border-bottom: none; }
    i{ margin-right: 5px; }
    span{ font-size:13px; }
    .fa-exclamation-triangle{ color: #e2c84a;}
    .fa-map-marker{ color: #ea5c5c;}
    &:hover{
      background: #0D79F2;
      color: #fff;

      i{ color: #fff; }
    }
    &.empty{ cursor: default; }
  }
}

.color-input-box{
  position: relative;

  .palette{
    border-radius: 3px;
    position: absolute;
    top: 50%;
    right: 10px;
    margin-top: -10px;
    width: 20px;
    height: 20px;
    z-index: 2;
    cursor: pointer;
    box-shadow:inset 0 0 1px 1px rgba(0,0,0, .25);
  }
}
.slide-overlay-color{
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 2;
}

.upload-row{
  position: relative;
  z-index: 2;

  &.below{ z-index: 1; }
}
.upload-loading-bg{
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0, .75);
}
.upload-box{
  position: relative;
  border:1px solid #ffc23a;
  background: #fffee9;
  padding: 10px;
  font-size: 14px;
  margin: 20px 0;
  display: flex;

  .upload-file{
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    font-size: 13px;
    line-height: 24px;
  }
  .button{
    padding: 6px 10px;
    margin-left: 5px;
    float: right;
    text-transform: uppercase;
  }
  &.airline{
    border:1px solid #48e02c;
    background: #eeffe9;
  }
  &.has-message{ margin-bottom: 15px; }
}
.upload-message{
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  color: #2bc74d;
  font-weight: 600;

  &.error{ color: #f20d0d; }
}

.loader{
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0, .5);
  z-index: 3;

  .modal-table {
    display: table;
    width: 100%;
    height: 100%;
  }
  .modal-content {
    display: table-cell;
    vertical-align: middle;
  }
  .box{
    margin: auto;
    background: #fff;
    width: 400px;
    text-align: center;
    padding: 20px;
    position: relative;

    #scrollHere{
      position:absolute;
      top: -60px;
      left: 0;
      width: 100%;
      height: 20px;
    }
    i{
      vertical-align: middle;
      margin-right: 5px;
    }
    .fa-spinner{ color: #0d79f2; }
    .response{
      display: block;
      margin-bottom: 10px;
    }
    .fa-check-circle{ color: #59e484; }
    .fa-times-circle{ color: #f13434; }
    span{
      display: inline-block;
      vertical-align: middle;
      font-weight: 600;
      color: #555;
    }
    .message{
      display: block;
      margin-bottom: 20px;
    }
  }
}

.error-message {
  margin-top: 10px;
  font-size: 13px;
  font-weight: 700;
  border: 1px solid #F20D0D;
  border-radius: 3px;
  color: #F20D0D;
  padding: 15px;

  & ul {
    margin-bottom: 0;
    margin-top: 5px;
  }
}

.padding-L { padding-left: 7px; }
.padding-R { padding-right: 7px; }
.margin-T { margin-top: 15px; }
.margin-B { margin-bottom: 10px; }

.expand-enter-active, .expand-leave-active { transition: all .3s ease; }
.expand-enter, .expand-leave-to {
  transform: scale(.8);
  opacity: 0;
}

.slide-down-enter-active, .slide-down-leave-active { transition: all .3s ease; }
.slide-down-enter, .slide-down-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

@media only screen and (max-width: 430px) {
  .hideMobile {
    display: none;
  }
  .padding-R, .padding-L {
    padding: 0;
  }

  .small-2, .small-5, .small-6, .small-3 {
    width: 100%;
  }

  .small-offset-8, .small-offset-6, .small-offset-4 {
    margin: 0;
  }
  
  .geofence-fields {
    padding: 12px;
  }
  
  .heading {
    display: flex;
    flex-direction: column-reverse;
  }

  .geofence-controls {
    display: flex;
    justify-content: center;
    gap: 5px;
    &.service-area {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
    }

  }
}
</style>



// WEBPACK FOOTER //
// src/components/Geofences/GeofenceFields.vue